import { useState, type FormEvent } from "react";
import { Badge, Banner, Button, Card, EmptyState, Icon, ListGroup, ListRow, Select, TextField, useToast, type IconName } from "@vavr/ui";
import { api, ApiError } from "../shared/api.ts";
import { useAsync } from "../shared/useAsync.ts";
import { useGuardian } from "./context.tsx";
import { dayKey, type GuardianSite } from "./status.ts";

export interface GuardianRequest { id: string; kind: "absence" | "early_pickup" | "note"; child_id: string; for_date: string; detail: string | null; status: string; created_at: number }

export const REQUEST_LABEL: Record<string, string> = { absence: "Absent", early_pickup: "Early pickup", note: "Note" };

const prettyDate = (iso: string) => new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${iso}T12:00:00Z`));

export function RequestList({ s, requests }: { s: GuardianSite; requests: GuardianRequest[] }) {
  const names = new Map(s.children.map((c) => [c.id, c.first]));
  if (!requests.length) return null;
  return (
    <ListGroup id={`sent-${s.site.id}`} title="Sent to the office" description={s.site.name}>
      {requests.map((r) => (
        <ListRow
          key={r.id}
          leading={{ icon: r.kind === "absence" ? "calendar" : r.kind === "early_pickup" ? "leave" : "note", tone: r.kind === "absence" ? "warning" : "brand" }}
          title={names.get(r.child_id) ?? "Child"}
          subtitle={<>{`${REQUEST_LABEL[r.kind]} · ${prettyDate(r.for_date)}`}{r.detail && <><br />{r.detail}</>}</>}
          trailing={<Badge tone={r.status === "done" ? "success" : r.status === "declined" ? "warning" : "neutral"} dot>{r.status === "open" ? "Waiting" : r.status === "done" ? "Seen" : "Declined"}</Badge>}
        />
      ))}
    </ListGroup>
  );
}

/** A few big, tappable choices instead of a menu: every option is visible at once. */
function Choice({ label, value, options, onChange }: { label: string; value: string; options: { value: string; label: string; icon?: IconName }[]; onChange: (v: string) => void }) {
  return (
    <fieldset className="pmu-g-choice">
      <legend className="pmu-field__label">{label}</legend>
      <div className="pmu-g-choice__options">
        {options.map((o) => (
          <button key={o.value} type="button" className="pmu-g-choice__option" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
            {o.icon && <Icon name={o.icon} />}{o.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/** Absence and early-pickup requests with a date and a note (plan §7). */
export function Requests() {
  const { overview, version } = useGuardian();
  const sites = overview.sites.filter((s) => s.children.length);
  const [siteId, setSiteId] = useState(sites[0]?.site.id ?? "");
  const s = sites.find((x) => x.site.id === siteId);
  const [f, setF] = useState({ child: s?.children[0]?.id ?? "", kind: "absence", date: s ? dayKey(Date.now(), s.site.tz) : "", detail: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const list = useAsync(() => (siteId ? api<{ requests: GuardianRequest[] }>("GET", `/api/v1/guardian/sites/${siteId}/requests`) : Promise.resolve({ requests: [] })), [siteId, version]);
  if (!s) return <EmptyState icon="list" title="No children linked yet" />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api("POST", `/api/v1/guardian/sites/${siteId}/requests`, { child_id: f.child, kind: f.kind, for_date: f.date, detail: f.detail || null });
      toast.show({ title: "Sent to the office", tone: "success" });
      setF((x) => ({ ...x, detail: "" }));
      list.reload();
    } catch (err) { setError((err as ApiError).message); } finally { setBusy(false); }
  }

  return (
    <div className="pmu-stack">
      <Card className="pmu-g-formcard" title="Absent or leaving early?" description="The office and the class teacher see it right away.">
      <form className="pmu-stack" onSubmit={submit}>
        {error && <Banner tone="danger" title={error} />}
        {sites.length > 1 && <Select label="School or center" value={siteId} onValueChange={(v) => setSiteId(v ?? "")} options={sites.map((x) => ({ value: x.site.id, label: x.site.name }))} />}
        {s.children.length > 1 && (
          <Choice label="Child" value={f.child} onChange={(v) => setF((x) => ({ ...x, child: v }))} options={s.children.map((c) => ({ value: c.id, label: c.first }))} />
        )}
        <Choice label="What" value={f.kind} onChange={(v) => setF((x) => ({ ...x, kind: v }))} options={[
          { value: "absence", label: "Will be absent", icon: "calendar" }, { value: "early_pickup", label: "Early pickup", icon: "leave" },
        ]} />
        <TextField label="Date" type="date" required value={f.date} onChange={(e) => setF((x) => ({ ...x, date: e.target.value }))} />
        <TextField label={f.kind === "early_pickup" ? "Time and who is coming" : "Note (optional)"} value={f.detail} maxLength={500} onChange={(e) => setF((x) => ({ ...x, detail: e.target.value }))} placeholder={f.kind === "early_pickup" ? "1:30 pm, Grandpa Joe" : "Doctor's appointment"} />
        <Button type="submit" size="lg" icon="upload" loading={busy} disabled={!f.child} fullWidth>Send to the office</Button>
      </form>
      </Card>
      {list.data && <RequestList s={s} requests={list.data.requests} />}
    </div>
  );
}
